import {
  Leaf,
  Bug,
  Scissors,
  Trees,
  Sprout,
  TreePine,
  Rat,
  Spline,
  Worm,
  Cog,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "../lib/i18n";

const PAYSAGISTE_ICONS = [Sprout, Leaf, TreePine, Scissors, Trees, Cog];
const NUISIBLES_ICONS = [Bug, Bug, Bug, Bug, TreePine, Worm, Spline, Rat];

function ServiceCard({ Icon, title, desc, idx, variant }) {
  return (
    <div
      data-testid={`service-card-${variant}-${idx}`}
      className="group bg-white rounded-3xl p-7 border border-[#E5EBE5] hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
    >
      <div className="w-14 h-14 rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-5 group-hover:bg-[#1B5E20] transition-colors">
        <Icon className="w-7 h-7 text-[#1B5E20] group-hover:text-white transition-colors" />
      </div>
      <h3 className="font-['Outfit'] text-xl font-semibold text-[#1A2E1C] mb-2">
        {title}
      </h3>
      <p className="text-[#5C705E] leading-relaxed">{desc}</p>
    </div>
  );
}

export default function Services() {
  const { t } = useT();
  const paysagiste = t("services.paysagiste");
  const nuisibles = t("services.nuisibles");

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-gradient-to-b from-[#E8F5E9]/40 via-transparent to-transparent"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <div className="text-sm tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-4">
            {t("services.eyebrow")}
          </div>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-light tracking-tight text-[#1A2E1C] leading-tight">
            {t("services.title1")}{" "}
            <span className="text-[#1B5E20] font-medium">
              {t("services.title2")}
            </span>
          </h2>
        </div>

        {/* Paysagiste */}
        <div className="mb-20" data-testid="paysagiste-block">
          {/* Crédit d'impôt mention */}
          <div
            data-testid="credit-impot-badge"
            className="mb-6 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#E8F5E9] border border-[#1B5E20]/20"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1B5E20] text-white">
              <span className="font-['Outfit'] font-bold text-sm">-50%</span>
            </div>
            <div>
              <div className="font-['Outfit'] font-semibold text-[#1A2E1C] text-sm">
                {t("services.creditTitle")}
              </div>
              <div className="text-xs text-[#5C705E]">
                {t("services.creditSub")}
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl mb-10 aspect-[16/5] md:aspect-[16/4]">
            <img
              src="/img/photos/entretien-jardin.webp"
              alt="Tondeuse autoportée professionnelle K'REUSE EXTÉRIEUR"
              width="1600"
              height="2400"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A2E1C]/85 via-[#1A2E1C]/55 to-transparent" />
            <div className="absolute inset-0 flex items-center px-8 md:px-12">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg">
                  <Leaf className="w-7 h-7 text-[#1B5E20]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#A5D6A7] font-medium mb-1">
                    {t("services.paysagisteEyebrow")}
                  </div>
                  <h3 className="font-['Outfit'] text-2xl md:text-3xl font-medium text-white">
                    {t("services.paysagisteTitle")}
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paysagiste.map((s, i) => (
              <ServiceCard
                key={s.title}
                Icon={PAYSAGISTE_ICONS[i]}
                title={s.title}
                desc={s.desc}
                idx={i}
                variant="paysagiste"
              />
            ))}
          </div>
        </div>

        {/* Nuisibles */}
        <div data-testid="nuisibles-block">
          {/* Certibiocide certification */}
          <div
            data-testid="certibiocide-badge"
            className="mb-6 inline-flex items-center gap-4 px-5 py-3 rounded-2xl bg-white border border-[#A5D6A7]/60 shadow-sm"
          >
            <img
              src="/img/certibiocide.webp"
              alt="Logo Certibiocide — Agrément d'État"
              width="371"
              height="136"
              className="h-12 w-auto object-contain"
              loading="lazy"
              decoding="async"
            />
            <div className="border-l border-[#E5EBE5] pl-4">
              <div className="font-['Outfit'] font-semibold text-[#1A2E1C] text-sm">
                {t("services.certiTitle")}
              </div>
              <div className="text-xs text-[#5C705E]">
                {t("services.certiSub")}
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl mb-10 aspect-[16/5] md:aspect-[16/4]">
            <img
              src="/img/photos/nid-frelons.webp"
              alt="Nid de frelons asiatiques à détruire en sécurité en Creuse"
              width="1600"
              height="2240"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A2E1C]/85 via-[#1A2E1C]/55 to-transparent" />
            <div className="absolute inset-0 flex items-center px-8 md:px-12">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg">
                  <Bug className="w-7 h-7 text-[#1B5E20]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#A5D6A7] font-medium mb-1">
                    {t("services.nuisiblesEyebrow")}
                  </div>
                  <h3 className="font-['Outfit'] text-2xl md:text-3xl font-medium text-white">
                    {t("services.nuisiblesTitle")}
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nuisibles.map((s, i) => (
              <ServiceCard
                key={s.title}
                Icon={NUISIBLES_ICONS[i]}
                title={s.title}
                desc={s.desc}
                idx={i}
                variant="nuisibles"
              />
            ))}
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/entreprise-nuisibles-creuse"
              data-testid="services-pest-pillar-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white text-sm font-medium transition-all"
            >
              Anti-nuisibles Creuse
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/desinsectisation-creuse"
              data-testid="services-pest-desinsectisation-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white text-sm font-medium transition-all"
            >
              Désinsectisation
            </Link>
            <Link
              to="/desinfection-creuse"
              data-testid="services-pest-desinfection-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white text-sm font-medium transition-all"
            >
              Désinfection
            </Link>
            <Link
              to="/punaises-de-lit-creuse"
              data-testid="services-pest-punaises-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white text-sm font-medium transition-all"
            >
              Punaises de lit
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
